import { message as aMessage } from "ant-design-vue";
import { getEnvironment } from "@/src/utils/utils.js";
import { bannerStatuses } from "@/src/constants/bannerConstants.js";
import { redirectToLeadsOrContacts } from "@/src/utils/callsUtils.js";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import {
  callStatuses,
  conferenceParticipantLabels,
} from "@/src/constants/callConstants.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import mqttService from "@/src/services/mqtt/mqttService.js";
import BaseTelephonyEventHandler from "@/src/services/telephony/base/baseTelephonyEventHandler.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";

class PlivoEventHandler extends BaseTelephonyEventHandler {
  constructor() {
    super(telephonyProviders.plivo);
    this.mqttInitialized = false;
    this.handlerInitialized = false;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.handleMQTTReady = null;
    this.campaignCallSid = null;
  }

  get campaignTopic() {
    return `${getEnvironment()}_plivo_events`;
  }

  async initialize(device) {
    // Singleton shared across the SDK (PlivoDevice) and iframe (PlivoIframeDevice)
    // devices. When a workspace has both domestic + international Plivo, this can be
    // called twice — guard so we don't double-register the mqtt-connected listener
    // or double-subscribe to the campaign topics (which would double-fire the pickup).
    if (this.handlerInitialized) {
      if (device) this.device = device;
      return;
    }
    this.handlerInitialized = true;

    this.device = device;
    console.log("Plivo event handler initialized");

    try {
      this.handleMQTTReady = async () => {
        await this.subscribeToTopics();
      };

      eventBus.on("mqtt-connected", this.handleMQTTReady);
      
      if (mqttService.isConnected) {
        await this.subscribeToTopics();
      }

      this.setupMessageListeners();
      this.setupDeviceListeners();
    } catch (error) {
      console.error("Failed to initialize Plivo event handler:", error);
      console.log("Continuing without MQTT for Plivo event handler");
    }
  }

  async subscribeToTopics() {
    try {
      await mqttService.subscribe(this.campaignTopic, (message, topic) =>
        this.handleMqttMessage(message, topic),
      );

      await mqttService.subscribe(
        `calls/events/campaign_pickup`,
        (message, topic) => this.handleMqttMessage(message, topic),
      );

      await mqttService.subscribe(
        `calls/status/+`,
        (message, topic) => this.handleMqttMessage(message, topic),
      );

      console.log("Successfully subscribed to Plivo MQTT topics");
      this.mqttInitialized = true;
    } catch (error) {
      console.error("Failed to subscribe to Plivo topics:", error);
    }
  }


  setupMessageListeners() {
    console.log("Setting up Plivo message listeners");
  }

  setupDeviceListeners() {
    console.log("Setting up Plivo device listeners");

    if (!this.device) return;
  }

  registerConnectionMessageHandlers(connection) {
    console.log("Registering Plivo connection message handlers");
  }

  async sendMessage(connection, message) {
    try {
      if (!this.mqttInitialized) {
        console.warn("MQTT not initialized, cannot send message");
        return null;
      }

      const topic = `calls/events/plivo_outbound`;
      const messagePayload = {
        timestamp: new Date().toISOString(),
        connection_id: connection?.parameters?.CallSid || "unknown",
        message: message,
        provider: "plivo",
        event_type: "outbound_message",
      };

      await mqttService.publish(topic, messagePayload);
      console.log("Message sent via MQTT:", messagePayload);
      return messagePayload;
    } catch (error) {
      console.error("Failed to send message via MQTT:", error);
      throw error;
    }
  }

  async handleMqttMessage(message, topic) {
    try {
      console.log(`Received MQTT message from topic ${topic}:`, message);

      if (topic === this.campaignTopic || topic.includes("campaign_pickup")) {
        await this.handleCampaignMessage(message);
      }

      if (topic.startsWith("calls/status/")) {
        await this.handleCallStatusMessage(message, topic);
      }

      if (topic.startsWith("calls/events/")) {
        await this.handleCallEventMessage(message, topic);
      }

      if (topic.includes("call-events/workspace/")) {
        await this.handleWorkspaceCallEvent(message, topic);
      }

      await this.handleCustomMessage(message);
    } catch (error) {
      console.error(`Error handling MQTT message from topic ${topic}:`, error);
    }
  }

  async handleCallEventMessage(message, topic) {
    try {
      console.log("Handling call event message:", message);

      const eventData = message?.data || message;

      if (eventData?.event_type === "call_answered") {
        console.log("Call answered event received via MQTT");
      } else if (eventData?.event_type === "call_ended") {
        console.log("Call ended event received via MQTT");
      }
    } catch (error) {
      console.error("Error handling call event message:", error);
    }
  }


  async handleCallStatusMessage(message, topic) {
    try {
      console.log("Handling call status message:", message);
      if (message?.status === "answered") {
        console.log("Call answered via MQTT");
      } else if (message?.status === "completed") {
        console.log("Call completed via MQTT");
      }
    } catch (error) {
      console.error("Error handling call status message:", error);
    }
  }

  async handleCampaignMessage(message) {
    try {
      const eventData = message?.data || message;
      const workspaceId = WorkspaceService.getCurrentWorkspaceId();
      const userId = localStorage.getItem("userId");

      const campaignPickupKey = `${workspaceId}/${userId}/campaign_pickup`;
      const campaignUpdateKey = `${workspaceId}/${userId}/campaign_call_update`;

      if (eventData?.[campaignPickupKey]) {
        const campaignData = eventData[campaignPickupKey];

        if (campaignData?.call_sid) {
          this.campaignCallSid = campaignData.call_sid;
        }
        
        await store.dispatch("callsCampaign/updateCallOngoing", true);
        await store.dispatch(
          "dynamicBanner/updateBannerStatus",
          bannerStatuses.CONNECTED,
        );
        eventBus.emit("call-campaign-pickup");
        await redirectToLeadsOrContacts(campaignData);
      }

      if (eventData?.[campaignUpdateKey]) {
        await store.dispatch("callsCampaign/refreshCampaignDetails");
      }

      if (eventData?.workspace_id && eventData?.call_id && eventData?.status) {
        this.handleBackendCallEvent(eventData);
      }
    } catch (error) {
      console.error("Error handling campaign message:", error);
    }
  }

  handleBackendCallEvent(eventData) {
    try {
      const { workspace_id, call_id, status, entity_details } = eventData;
      
      if (status === callStatuses.RINGING && entity_details && entity_details.length > 0) {
        const entityData = entity_details[0];
        console.log("Incoming call entity found:", entityData);
      }
    } catch (error) {
      console.error("Error handling backend call event:", error);
    }
  }

  async handleCustomMessage(message) {
    try {
      console.log("Handling Plivo custom message:", message);

      const messageData = message?.data || message;

      if (messageData?.name === "caller_on_hold") {
        await store.dispatch("conferenceCall/setTransferringCall", false);
        await store.dispatch(
          "conferenceCall/setCallerOnHoldEventReceived",
          true,
        );

        if (messageData.participants) {
          await store.dispatch(
            "conferenceCall/setParticipants",
            messageData.participants,
          );
        }

        if (messageData.transfer_number) {
          await store.dispatch("conferenceCall/addParticipant", {
            conference_sid: messageData.conference_id,
            label: conferenceParticipantLabels.TRANSFER_TARGET,
            status: callStatuses.PENDING,
            hold: false,
            muted: false,
            phone_number: messageData.transfer_number,
            name: messageData.transfer_name,
            end_conference_on_exit: true,
            start_conference_on_enter: true,
          });
        }
      }

      if (messageData?.transfer_target_join) {
        await store.dispatch("conferenceCall/setTransferTargetActive", true);
      }

      if (messageData?.transfer_target_left) {
        const wasActive = store.getters["conferenceCall/isWarmTransfer"];
        eventBus.emit("cancel-transfer-abort");
        await store.dispatch(
          "conferenceCall/cancelWarmTransfer",
          conferenceParticipantLabels.TRANSFER_TARGET,
        );
        if (wasActive) {
          aMessage.info("Transfer ended");
        }
      }

      if (messageData?.transfer_complete) {
        await store.dispatch("conferenceCall/addParticipant", {
          ...messageData.transfer_complete,
          name: messageData.transfer_complete?.name?.trim(),
          hold: false,
          label: conferenceParticipantLabels.TRANSFER_TARGET,
        });
      }

      if (messageData?.plivo_event) {
        console.log("Handling Plivo-specific event:", messageData.plivo_event);
      }
    } catch (error) {
      console.error("An error occurred while handling custom message: ", error);
    }
  }

  handleStatusUpdate(content) {
    console.log("Plivo status update:", content);
  }

  async cleanup() {
    try {
      if (this.mqttInitialized) {
        await mqttService.unsubscribe(this.campaignTopic);
        await mqttService.unsubscribe("calls/events/campaign_pickup");
        await mqttService.unsubscribe("calls/status/+");
        await mqttService.unsubscribe("calls/events/+");
        console.log("Unsubscribed from Plivo topics");
      }
      if (this.handleMQTTReady) {
        eventBus.off("mqtt-connected", this.handleMQTTReady);
        this.handleMQTTReady = null;
      }
      this.campaignCallSid = null; 
    } catch (error) {
      console.error("Error during Plivo event handler cleanup:", error);
    }
  }
  
  getCampaignCallSid() {
    return this.campaignCallSid;
  }
  
  clearCampaignCallSid() {
    this.campaignCallSid = null;
  }
}

const plivoEventHandler = new PlivoEventHandler();
export default plivoEventHandler;
